import type { docsHttpService, OrderDoc } from "@megaron/docs-contracts";
import type { HttpServiceClient } from "@megaron/http-service-client";

type DocsClient = HttpServiceClient<typeof docsHttpService>;

const uniqueTrimmedStrings = (values: string[]): string[] => {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const value of values) {
    const trimmed = value.trim();
    if (!trimmed || seen.has(trimmed)) continue;
    seen.add(trimmed);
    result.push(trimmed);
  }
  return result;
};

export const fetchInvoiceNumberSuggestions = async (
  docs: DocsClient,
  searchText: string,
): Promise<string[]> => {
  const result = await docs.searchOrders({ searchText, limit: 20 });
  if (result.isFailure) return [];
  const normalizedSearch = searchText.trim().toLowerCase();
  const invoiceNumbers = result.value.items.flatMap((order) => order.invoiceNumbers);
  return uniqueTrimmedStrings(
    invoiceNumbers.filter(
      (invoiceNumber) =>
        !normalizedSearch || invoiceNumber.trim().toLowerCase().includes(normalizedSearch),
    ),
  );
};

export const fetchOrderNumberSuggestions = async (docs: DocsClient, searchText: string): Promise<string[]> => {
  const result = await docs.searchOrders({ searchText, limit: 10 });
  if (result.isFailure) return [];
  return uniqueTrimmedStrings(result.value.items.map((item) => item.number));
};

const orderMatchesInvoice = (order: OrderDoc, invoiceNumber: string): boolean =>
  order.invoiceNumbers.some((invoice) => invoice.trim() === invoiceNumber);

export const resolveOrderNumberForInvoice = async (
  docs: DocsClient,
  invoiceNumber: string,
): Promise<string | null> => {
  const trimmedInvoice = invoiceNumber.trim();
  if (!trimmedInvoice) return null;
  const result = await docs.searchOrders({ searchText: trimmedInvoice, limit: 20 });
  if (result.isFailure) return null;
  const match = result.value.items.find((order) => orderMatchesInvoice(order, trimmedInvoice));
  return match?.number ?? null;
};

export const resolveInvoiceNumberForOrder = async (
  docs: DocsClient,
  orderNumber: string,
): Promise<string | null> => {
  const trimmedOrder = orderNumber.trim();
  if (!trimmedOrder) return null;
  const result = await docs.searchOrders({ searchText: trimmedOrder, limit: 10 });
  if (result.isFailure) return null;
  const match = result.value.items.find((order) => order.number === trimmedOrder);
  if (!match) return null;
  const invoice = match.invoiceNumbers.find((number) => number.trim().length > 0);
  return invoice?.trim() ?? null;
};
